<template>
    <div class="headerTop">
        <!-- has_center_actions habilita el layout responsive de 2 filas (los
            botones del slot arriba de todo) sólo cuando el slot existe — sin
            la clase, el clasificador no gana una fila fantasma ni su row-gap. -->
        <div class="header_section_header"
            :class="{ has_center_actions: !!($scopedSlots['center-actions'] || $slots['center-actions']) }">
            <div class="left_aligned_title">
                <div class="header_title">{{ title }}</div>
                <div class="header_description">{{ description }}</div>
            </div>
            <!-- Acciones centradas a la altura de los selects (Tipo Contraparte /
                Rango de tiempo). Celda central del grid del header: centrado en
                flujo (nunca se solapa con los selects) y en pantallas angostas
                baja a su propia fila. Sólo lo llena DashboardErp; el v-if evita
                una fila fantasma (row-gap) en el clasificador. El check va
                inline sobre $scopedSlots/$slots (propiedades de la instancia,
                no del script) — así tampoco depende del orden de aplicación
                de un hot-update template/script. -->
            <div v-if="$scopedSlots['center-actions'] || $slots['center-actions']"
                class="center_actions">
                <slot name="center-actions" />
            </div>
            <div class="right_aligned_buttons ml-2">
                <div v-if="!hideTypeFilter" class="button_tipo_contraparte">
                    <div style="min-width: 180px">
                        <SelectComponent :value="selectedType" @input="(v) => $emit('update:selectedType', v)"
                            label="Tipo Contraparte" :weirdOptions="null" :options="typeOptions" />
                    </div>
                </div>
                <div class="button_tipo_contraparte">
                    <div style="min-width: 230px">
                        <SelectComponent :value="selectedRange" @input="(v) => $emit('update:selectedRange', v)"
                            currentIcon="calendar" label="Rango de tiempo"
                            :options="periodDataSelect" :weirdOptions="null" />
                    </div>
                </div>
                <div v-if="localTypeFiles.length > 0" class="button_tipo_contraparte is-justify-content-end">
                    <div style="min-width: 230px">
                        <SelectComponent :value="selectedUploadFile"
                            @input="(v) => $emit('update:selectedUploadFile', v)" :staticLabel="'Subir Documentos'"
                            theme="classification_theme" :weirdOptions="null" currentIcon="UploadFile"
                            :options="localTypeFiles" />
                    </div>
                </div>
                <div v-if="showDownloadButton" :class="['is-align-content-end', 'is-clickable', {'is-disabled-local': downloadDisabled}]">
                    <span class="btnNoClassify is-flex is-justify-content-center is-clickable mr-2"
                        :class="{
                            'theme_dark_btn': isDownloadingFile,
                            'theme_default_btn': !isDownloadingFile}"
                        @click="$emit('download')">
                        <DownloadFile class="ecs" />
                        <!-- Bajar documentos -->
                    </span>
                </div>
            </div>
        </div>
        <div>
            <div class="counterpart_table_header">
                <div class="search_container">
                    <SearchIcon class="search_icon" />
                    <input type="text" class="search_input " :value="searchFilter"
                        @input="(e) => $emit('update:searchFilter', e.target.value)"
                        placeholder="Buscar por nombre o rut" />
                    <button v-if="searchFilter" class="clear_button" @click="$emit('update:searchFilter', '')">
                        <Ecs class="ecs" />
                    </button>
                </div>
                <div v-if="!hideUnclassifiedFilter" class="filter_slot">
                    <b-checkbox :class="{
                            'theme_dark_btn': filterTable != '',
                            'theme_default_btn': filterTable == ''}"
                        class="btnNoClassify is-full-width" true-value="Sin Clasificar" :false-value="''"
                        :key="filterTable" :value="filterTable"
                        @input="(v) => $emit('update:filterTable', v)">
                        <span class="pl-0">Sin Clasificar</span>
                    </b-checkbox>
                </div>
                <div class="filter_slot">
                    <b-checkbox :class="{
                            'theme_dark_btn': filterTableHasAccrual != '',
                            'theme_default_btn': filterTableHasAccrual == ''}"
                            class="btnNoClassify is-full-width" true-value="Devengados" :false-value="''"
                        :key="filterTableHasAccrual" :value="filterTableHasAccrual"
                        @input="(v) => $emit('update:filterTableHasAccrual', v)">
                        <span class="pl-0">
                            Devengados
                            <CalendarMinus class="ecsFilter" :class="{'iconWhite': filterTableHasAccrual != ''}"/>
                        </span>
                    </b-checkbox>
                </div>
                <div class="filter_slot" v-if="allFilterCategories.length >0">
                    <SelectComponent ref="SelectCategoryComponent" :staticLabel="'Categorizados'" :hasSearchBox="true"
                        theme="classification_theme" :isSearchable="true" :weirdOptions="allFilterCategories"
                        iconRight="DashboardSquare"
                        @selected-values="(e) => $emit('selected-categories', e)" :isAccount="true" />
                </div>
                <div class="filter_slot">
                    <SelectComponent ref="SelectAccountComponent" :staticLabel="'Clasificados'" :hasSearchBox="true"
                        iconRight="RobotFace"
                        theme="classification_theme" :isSearchable="true" :weirdOptions="allAccountsOnDocs"
                        @selected-values="(e) => $emit('selected-accounts', e)" :isAccount="true" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Ecs from "@/components/svg/Ecs.vue";
import SearchIcon from "@/components/svg/SearchIcon.vue";
import CalendarMinus from "@/components/svg/CalendarMinus.vue";
import DownloadFile from "@/components/svg/DownloadFile.vue";
import SelectComponent from "@/components/classification/SelectComponent";

export default {
    name: "ClassificationHeader",
    components: {
        Ecs,
        SearchIcon,
        CalendarMinus,
        DownloadFile,
        SelectComponent,
    },
    props: {
        typeOptions: { type: Array, default: () => [] },
        periodDataSelect: { type: Array, default: () => [] },
        localTypeFiles: { type: Array, default: () => [] },
        selectedType: { type: String, default: "todos" },
        selectedRange: { type: String, default: "" },
        selectedUploadFile: { type: String, default: "" },
        searchFilter: { type: String, default: "" },
        filterTable: { type: String, default: "" },
        filterTableHasAccrual: { type: String, default: "" },
        // El filtro "Sin Clasificar" no aplica en el Libro Mayor (ERP): todos los
        // docs ya vienen clasificados por documento, así que nunca hay entradas
        // sin clasificar. La vista ERP lo oculta; el clasificador editable no.
        hideUnclassifiedFilter: { type: Boolean, default: false },
        // Oculta el select "Tipo Contraparte" (Libro Mayor en alcance Balance:
        // las contrapartes no aplican a las cuentas de balance).
        hideTypeFilter: { type: Boolean, default: false },
        allFilterCategories: { type: Array, default: () => [] },
        allAccountsOnDocs: { type: Array, default: () => [] },
        getUrlDriveClassification: { type: String, default: "" },
        isDownloadingFile: { type: Boolean, default: false },
        // Título/descripción configurables: la vista Libro Mayor (solo lectura)
        // muestra su propio encabezado. Defaults = textos del clasificador.
        title: { type: String, default: "Clasificación de Documentos" },
        description: {
            type: String,
            default: "Consulta, clasifica y organiza toda la información financiera de tu negocio.",
        },
        // Solo lectura: oculta el botón de descarga (el clasificador editable lo
        // muestra siempre; la vista Libro Mayor lo pide explícito con showDownload).
        readOnly: { type: Boolean, default: false },
        // Fuerza el botón de descarga aun en read-only (Libro Mayor/ERP): baja
        // toda la data del mayor como Excel re-subible. En ese modo no depende de
        // getUrlDriveClassification (Drive), sólo del estado de descarga.
        showDownload: { type: Boolean, default: false },
    },
    computed: {
        showDownloadButton() {
            return !this.readOnly || this.showDownload;
        },
        // El clasificador se deshabilita sin URL de Drive; el ERP (showDownload)
        // sólo mientras descarga (genera el archivo en el backend).
        downloadDisabled() {
            if (this.isDownloadingFile) return true;
            return !this.showDownload && this.getUrlDriveClassification === "";
        },
    },
    methods: {
        // Expuestos al padre (que recibe los eventos clear/delete desde la tabla)
        // para manejar el estado de checkboxes de los SelectComponent por ref.
        clearAccountFilter() {
            if (this.$refs.SelectAccountComponent) {
                this.$refs.SelectAccountComponent.clearCbList();
            }
        },
        clearCategoryFilter() {
            if (this.$refs.SelectCategoryComponent) {
                this.$refs.SelectCategoryComponent.clearCbList();
            }
        },
        deleteAccountItem(item) {
            if (this.$refs.SelectAccountComponent) {
                this.$refs.SelectAccountComponent.deleteItemCbList(item);
            }
        },
        deleteCategoryItem(item) {
            if (this.$refs.SelectCategoryComponent) {
                this.$refs.SelectCategoryComponent.deleteItemCbList(item);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.headerTop {
    position: sticky;
    top: var(--app-chrome-top, 0px);
    width: 100%;
    background-color: var(--paper-soft);
    z-index: 5;
}

.header_section_header {
    padding-top: 20px;
    padding-bottom: 16px;
    // Grid de 3 columnas: título | acciones centradas (slot, opcional) |
    // selects. El centrado va EN FLUJO — a diferencia de un absolute, nunca
    // se solapa con los selects cuando el ancho no alcanza.
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    // end alinea título, slot y selects al mismo baseline, independientemente
    // de si tienen label flotando arriba o no.
    align-items: end;
    gap: 16px 24px;
    border-bottom: 1px solid var(--rule);
}

.left_aligned_title {
    grid-column: 1;
    min-width: 0;
}

.center_actions {
    grid-column: 2;
    justify-self: center;
}

.right_aligned_buttons {
    grid-column: 3;
    justify-self: end;
}

// Cuando el ancho no da para las 3 columnas (sidebar abierto, notebooks),
// las acciones del centro pasan a su propia fila ARRIBA de todo, siempre
// centradas; título y selects quedan en la fila de abajo. Sólo aplica con
// has_center_actions (vista ERP) — el clasificador mantiene una fila.
@media (max-width: 1280px) {
    .header_section_header {
        grid-template-columns: minmax(0, 1fr) auto;
    }

    .right_aligned_buttons {
        grid-column: 2;
    }

    .header_section_header.has_center_actions {
        .center_actions {
            grid-column: 1 / -1;
            grid-row: 1;
        }

        .left_aligned_title {
            grid-row: 2;
        }

        .right_aligned_buttons {
            grid-row: 2;
        }
    }
}

// Muy angosto (tablet / split view): todo apilado en una columna — el título
// recupera el ancho completo (compartiendo fila con los selects quedaba
// aplastado a una palabra por línea) y los selects envuelven debajo.
@media (max-width: 900px) {
    .header_section_header {
        grid-template-columns: minmax(0, 1fr);
    }

    .left_aligned_title,
    .center_actions,
    .right_aligned_buttons {
        grid-column: 1;
        grid-row: auto;
    }

    .right_aligned_buttons {
        justify-self: start;
        flex-wrap: wrap;
        width: 100%;

        // El botón de descarga (último del cluster) se pega al borde derecho
        // en vez de quedar suelto a la izquierda al envolver.
        > :last-child {
            margin-left: auto;
        }
    }

    // Con el sub-filtro presente (ERP) el orden es: filtro → título → selects.
    .header_section_header.has_center_actions {
        .center_actions {
            grid-row: 1;
        }

        .left_aligned_title {
            grid-row: 2;
        }

        .right_aligned_buttons {
            grid-row: 3;
        }
    }
}

.right_aligned_buttons {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 0;
}

.button_tipo_contraparte {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

.header_title {
    font-size: 1.5em;
    font-weight: 700;
    margin-bottom: 4px;
    color: var(--ink);
    line-height: 1.2;
}

.header_description {
    font-size: 1em;
    color: var(--ink-muted);
    line-height: 1.4;
}

.counterpart_table_header {
    background-color: var(--paper-soft);
    border-bottom: 1px solid var(--rule);
    // min-height (no height fija): en pantallas angostas los filtros
    // envuelven a una segunda fila y la caja crece — con altura fija se
    // derramaban por encima de la tabla.
    min-height: 60px;
    width: 100%;
    padding: 12px 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
}

// Slot de un filtro de la barra (Sin Clasificar / Devengados / Categorizados /
// Clasificados). Ancho fijo en desktop; bajo 768px pasan a fluidos de a dos
// por fila, así ninguno se recorta ni queda fuera del viewport.
.filter_slot {
    width: 200px;
    flex-shrink: 0;
}

@media (max-width: 768px) {
    // Grid en vez de flex-wrap: columnas SIEMPRE iguales (auto-fit), así los
    // filtros nunca quedan uno a media fila y otro estirado. El buscador
    // ocupa su propia fila completa; Devengados/Categorizados/Clasificados
    // se reparten en filas de columnas parejas según el ancho disponible.
    .counterpart_table_header {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    }

    .search_container {
        grid-column: 1 / -1;
    }

    .search_input {
        width: 100%;
        flex: 1;
    }

    .filter_slot {
        width: auto;
    }
}

.ecsFilter {
    height: 20px;
    overflow: hidden;
    flex-shrink: 0;
    margin-bottom: -5px;
}

.search_container {
    position: relative;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    // La separación con los filtros la da el gap del contenedor.
}

.search_icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    pointer-events: none;
    color: var(--ink-muted);
}

.search_input {
    width: clamp(100px, 25vw, 300px);
    padding: 8px 36px;
    background-color: var(--paper);
    border: 1px solid var(--rule-strong);
    border-radius: var(--radius);
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
    font-size: 14px;
    color: var(--ink);
    font-family: inherit;
    box-sizing: border-box;
}

.search_input::placeholder {
    color: var(--ink-faint);
}

.search_input:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-wash);
}

.clear_button {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--ink-faint);
    font-size: 16px;
    line-height: 1;
    padding: 4px;
    z-index: 2;
}

.clear_button:hover {
    color: var(--ink);
}

@media (max-width: 800px) {
    .search_input {
        width: 100%;
    }

    .counterpart_table_header {
        flex-direction: column;
        align-items: stretch;
        height: 120px;
    }
}

@media (max-width: 550px) {
    .counterpart_table_header {
        flex-direction: column;
        align-items: stretch;
        height: 210px;
    }
}

.theme_dark_btn{
    --background-color: #002e33;
    --background-hover: #185555;
    --text-color: #fff;
}

.theme_default_btn{
    --background-color: #FFF;
    --background-hover: #185555;
    --text-color: #333a48;
}

.btnNoClassify {
    padding: 10px 14px;
    align-items: center;
    gap: 8px;
    color: var(--Gray-900, #101828);
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;

    border-radius: 8px;
    border: 1px solid var(--Gray-300, #D0D5DD);
    background: var(--background-color, #FFF);
    color: var(--text-color, #000);

    /* Shadows/shadow-xs */
    box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
    height: 46px;
}

.btnNoClassify:hover{
    background: var(--background-hover);
    color: #fff;
    .ecsFilter, .ecs{
        filter: brightness(0) invert(1);
    }
}

.iconWhite {
    filter: brightness(0) invert(1);
}
</style>
